/**
 * Stage 5: CTA Placement — context-based version using StagePanel.
 *
 * Multi-step: loads CTA registry, LLM selects best CTA, inserts into article.
 * The three sub-actions are orchestrated as a single "Run" that chains them.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function renderOutput(doc: ArticleDocument) {
  const ctas = doc.article?.ctas;
  const markdown = doc.article?.markdown;
  if (!ctas || ctas.length === 0) {
    return <div className="text-sm text-gray-500">No CTAs placed yet.</div>;
  }
  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">Placed CTAs ({ctas.length})</div>
        {ctas.map((cta, i) => (
          <div key={i} className="border rounded p-2 text-xs border-gray-200 mb-1">
            <div className="font-medium text-gray-800">{cta.cta_id || cta.headline || 'CTA'}</div>
            {cta.body && <div className="text-gray-600">{cta.body}</div>}
            <div className="text-gray-400 mt-1">
              Placement: {cta.placement}{cta.button_text ? ` | Button: ${cta.button_text}` : ''}
            </div>
            {cta.shortcode && <code className="text-[10px] text-gray-400 mt-1 block">{cta.shortcode}</code>}
          </div>
        ))}
      </div>
      {markdown && (
        <details className="border border-gray-200 rounded">
          <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
            Article with CTAs ({markdown.split(/\s+/).filter(Boolean).length} words)
          </summary>
          <pre className="px-3 py-2 text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 max-h-96 overflow-y-auto">
            {markdown}
          </pre>
        </details>
      )}
    </div>
  );
}

export function Stage5CtaV2() {
  const { document } = useArticleContext();
  const hasMarkdown = Boolean(document?.article?.markdown);

  return (
    <StagePanel
      stageId="stage_5_cta_placement"
      title="Stage 5: CTA Placement"
      description="Selects and inserts call-to-action blocks into the article."
      runDisabled={!hasMarkdown}
      runDisabledReason={!hasMarkdown ? 'Requires finalized article markdown (Stage 4c)' : undefined}
      renderOutput={renderOutput}
    >
      {!hasMarkdown && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded border p-2">
          Waiting for article markdown from earlier stages.
        </div>
      )}
    </StagePanel>
  );
}
